import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { monthLabel, shiftMonth, todayYm } from "../../lib/events";

const LANES = 3;

function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Monday-first weeks covering the month. */
function buildWeeks(ym) {
  const [y, m] = ym.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const start = new Date(first);
  start.setDate(first.getDate() - ((first.getDay() + 6) % 7));
  const weeks = [];
  const cur = new Date(start);
  do {
    const days = [];
    for (let i = 0; i < 7; i++) { days.push(iso(cur)); cur.setDate(cur.getDate() + 1); }
    weeks.push(days);
  } while (cur.getMonth() === m - 1);
  return weeks;
}

function laneBars(week, events) {
  const wStart = week[0], wEnd = week[6];
  const bars = [];
  const lanes = [];
  const overlapping = events
    .filter((e) => e.start_date && e.start_date <= wEnd && (e.end_date || e.start_date) >= wStart)
    .sort((a, b) => a.start_date.localeCompare(b.start_date) || (b.end_date || b.start_date).localeCompare(a.end_date || a.start_date));
  for (const e of overlapping) {
    const s = e.start_date < wStart ? wStart : e.start_date;
    const en = (e.end_date || e.start_date) > wEnd ? wEnd : (e.end_date || e.start_date);
    const c0 = week.indexOf(s), c1 = week.indexOf(en);
    let lane = lanes.findIndex((busyUntil) => busyUntil < c0);
    if (lane === -1) { lane = lanes.length; lanes.push(-1); }
    lanes[lane] = c1;
    bars.push({ e, c0, c1, lane, continues: (e.end_date || e.start_date) > wEnd, started: e.start_date < wStart });
  }
  return bars;
}

export function EventCalendar({ events, dm, month, setMonth, today }) {
  const [hidden, setHidden] = useState({});
  const ym = month || todayYm();
  const weeks = useMemo(() => buildWeeks(ym), [ym]);
  const m = Number(ym.split("-")[1]);
  const c = dm
    ? { text: "text-slate-200", muted: "text-slate-500", cell: "border-slate-700/40", off: "text-slate-700", bar: "bg-cyan-400/20 text-cyan-100 hover:bg-cyan-400/35", today: "bg-cyan-400/10" }
    : { text: "text-[#191c1d]", muted: "text-[#6b7a7d]", cell: "border-[#e7e8e9]", off: "text-[#c5cfd1]", bar: "bg-[#006874]/12 text-[#004f58] hover:bg-[#006874]/22", today: "bg-[#006874]/6" };
  const inMonth = events.filter((e) => e.start_date && e.start_date.slice(0, 7) <= ym && (e.end_date || e.start_date).slice(0, 7) >= ym);
  return (
    <div data-testid="events-calendar" className={`rounded-2xl border overflow-hidden ${dm ? "bg-[#1a2324] border-slate-700/50" : "bg-white border-[#e7e8e9]"}`}>
      <div className={`flex items-center justify-between px-4 py-3 border-b ${c.cell}`}>
        <button onClick={() => setMonth(shiftMonth(ym, -1))} data-testid="calendar-prev" className={`p-1.5 rounded-full ${dm ? "hover:bg-white/6 text-slate-300" : "hover:bg-[#f3f4f5] text-[#3b494c]"}`} aria-label="Previous month"><ChevronLeft className="w-4 h-4" /></button>
        <div className="text-center">
          <p className={`text-sm font-semibold ${c.text}`} data-testid="calendar-month-label">{monthLabel(ym)}</p>
          <p className={`text-[11px] ${c.muted}`}>{inMonth.length} event{inMonth.length !== 1 ? "s" : ""}</p>
        </div>
        <button onClick={() => setMonth(shiftMonth(ym, 1))} data-testid="calendar-next" className={`p-1.5 rounded-full ${dm ? "hover:bg-white/6 text-slate-300" : "hover:bg-[#f3f4f5] text-[#3b494c]"}`} aria-label="Next month"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className={`grid grid-cols-7 text-[10px] font-bold uppercase tracking-wider px-2 pt-2 ${c.muted}`}>
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="text-center py-1">{d}</div>)}
      </div>
      <div className="px-2 pb-2">
        {weeks.map((week, wi) => {
          const bars = laneBars(week, inMonth);
          const overflow = bars.filter((b) => b.lane >= LANES);
          const key = `${ym}-${wi}`;
          const showAll = hidden[key];
          const visible = showAll ? bars : bars.filter((b) => b.lane < LANES);
          const rows = Math.max(1, (showAll ? Math.max(...bars.map((b) => b.lane), 0) + 1 : Math.min(LANES, Math.max(...bars.map((b) => b.lane), -1) + 1)));
          return (
            <div key={key} className={`relative border-t ${c.cell}`} data-testid={`calendar-week-${wi}`} style={{ minHeight: 34 + rows * 26 + (overflow.length && !showAll ? 18 : 0) }}>
              <div className="grid grid-cols-7">
                {week.map((d) => {
                  const off = Number(d.split("-")[1]) !== m;
                  return (
                    <div key={d} className={`px-1.5 pt-1 text-[11px] tabular-nums ${off ? c.off : c.text} ${d === today ? c.today : ""} ${d === today ? "font-bold" : ""}`}>
                      {Number(d.split("-")[2])}
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-7 gap-y-0.5 px-0.5" style={{ gridAutoRows: 24 }}>
                {visible.map((b) => (
                  <Link
                    key={`${b.e.id}-${wi}`}
                    to={`/events/${b.e.slug}`}
                    title={`${b.e.name} · ${b.e.city || ""}`}
                    data-testid={`calendar-bar-${b.e.id}`}
                    style={{ gridColumn: `${b.c0 + 1} / ${b.c1 + 2}`, gridRow: b.lane + 1 }}
                    className={`text-[11px] leading-6 px-2 truncate font-medium ${c.bar} ${b.started ? "rounded-l-none" : "rounded-l-md"} ${b.continues ? "rounded-r-none" : "rounded-r-md"}`}
                  >
                    {b.e.name}
                  </Link>
                ))}
              </div>
              {overflow.length > 0 && !showAll && (
                <button onClick={() => setHidden((h) => ({ ...h, [key]: true }))} data-testid={`calendar-more-${wi}`} className={`absolute right-2 bottom-0.5 text-[10px] font-semibold ${dm ? "text-cyan-300" : "text-[#006874]"}`}>
                  +{overflow.length} more
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default EventCalendar;
